import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/App";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

// ═══════════════════════════════════════════════════════════
// VERSPIELTE SVG ILLUSTRATIONEN (wie vorher)
// ═══════════════════════════════════════════════════════════

const HomeIcon = ({ size = 28, active }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <path d="M50 15 L15 50 L25 50 L25 80 L45 80 L45 60 L55 60 L55 80 L75 80 L75 50 L85 50 Z"
      fill={active ? C.green : "#7A6B5A"} />
    <rect x="43" y="58" width="14" height="22" rx="2" fill={active ? C.greenLight || "#4A7C59" : "#9A8B7A"} />
    {active && <>
      <circle cx="50" cy="42" r="3" fill="#FFF" />
      <path d="M44 48 Q50 54 56 48" stroke="#FFF" strokeWidth="2" fill="none" strokeLinecap="round" />
    </>}
  </svg>
);

const GiftNavIcon = ({ size = 28, active }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <rect x="22" y="48" width="56" height="38" rx="5" fill={active ? C.orange : "#7A6B5A"} />
    <rect x="18" y="36" width="64" height="16" rx="4" fill={active ? C.orange : "#7A6B5A"} />
    <rect x="46" y="36" width="8" height="50" fill={active ? (C.tanLight || "#E8C49A") : "#9A8B7A"} opacity="0.7" />
    <ellipse cx="42" cy="32" rx="10" ry="7" fill={active ? (C.tanLight || "#E8C49A") : "#9A8B7A"} />
    <ellipse cx="58" cy="32" rx="10" ry="7" fill={active ? (C.tanLight || "#E8C49A") : "#9A8B7A"} />
    {active && <>
      <circle cx="36" cy="64" r="3" fill="#FFF" />
      <circle cx="64" cy="64" r="3" fill="#FFF" />
      <path d="M42 74 Q50 80 58 74" stroke="#FFF" strokeWidth="2" fill="none" strokeLinecap="round" />
    </>}
  </svg>
);

const CoffeeNavIcon = ({ size = 28, active }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <path d="M28 38 L32 82 Q50 88 68 82 L72 38 Z" fill={active ? C.green : "#7A6B5A"} />
    <ellipse cx="50" cy="38" rx="24" ry="7" fill={active ? (C.greenLight || "#4A7C59") : "#9A8B7A"} />
    <path d="M72 44 Q84 48 84 58 Q84 68 72 64" stroke={active ? C.green : "#7A6B5A"} strokeWidth="5" fill="none" />
    {active && <>
      <circle cx="42" cy="58" r="3" fill="#FFF" />
      <circle cx="58" cy="58" r="3" fill="#FFF" />
      <path d="M44 68 Q50 74 56 68" stroke="#FFF" strokeWidth="2" fill="none" strokeLinecap="round" />
    </>}
  </svg>
);

const TableNavIcon = ({ size = 28, active }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <ellipse cx="50" cy="45" rx="32" ry="10" fill={active ? C.orange : "#7A6B5A"} />
    <rect x="44" y="50" width="12" height="28" rx="3" fill={active ? (C.orangeLight || "#F08A4D") : "#9A8B7A"} />
    {active && <>
      <circle cx="40" cy="40" r="3" fill="#FFF" />
      <circle cx="60" cy="40" r="3" fill="#FFF" />
      <path d="M44 47 Q50 51 56 47" stroke="#FFF" strokeWidth="2" fill="none" strokeLinecap="round" />
    </>}
  </svg>
);

const InvestNavIcon = ({ size = 28, active }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <ellipse cx="50" cy="58" rx="28" ry="22" fill={active ? C.green : "#7A6B5A"} />
    <ellipse cx="34" cy="42" rx="6" ry="8" fill={active ? C.green : "#7A6B5A"} />
    <ellipse cx="66" cy="42" rx="6" ry="8" fill={active ? C.green : "#7A6B5A"} />
    <rect x="42" y="30" width="16" height="4" rx="2" fill={active ? (C.tanLight || "#E8C49A") : "#9A8B7A"} />
    <circle cx="50" cy="24" r="5" fill={active ? (C.tanLight || "#E8C49A") : "#9A8B7A"} />
    {active && <>
      <circle cx="42" cy="54" r="3" fill="#FFF" />
      <circle cx="58" cy="54" r="3" fill="#FFF" />
      <path d="M44 64 Q50 70 56 64" stroke="#FFF" strokeWidth="2" fill="none" strokeLinecap="round" />
    </>}
  </svg>
);

// Smiley for profile
const SmileyIcon = ({ size = 28, active }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size }} fill={active ? C.green : "#7A6B5A"}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="8" cy="10" r="1.5" fill="#FDF6E9" />
    <circle cx="16" cy="10" r="1.5" fill="#FDF6E9" />
    <path d="M8 14c0 2.5 2 4 4 4s4-1.5 4-4" stroke="#FDF6E9" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

const TABS = [
  { id: "home", path: "/landing", label: "Home", Icon: HomeIcon },
  { id: "coupons", path: "/home?tab=coupons", label: "Coupons", Icon: GiftNavIcon, requiresAuth: true },
  { id: "stamps", path: "/home?tab=stamps", label: "Stempel", Icon: CoffeeNavIcon, requiresAuth: true },
  { id: "reservation", path: "/home?tab=reservation", label: "Tisch", Icon: TableNavIcon, requiresAuth: true },
  { id: "invest", path: "/home?tab=invest", label: "Invest", Icon: InvestNavIcon },
];

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setShowAuthModal } = useAuth();

  const currentTab = new URLSearchParams(location.search).get("tab") || "coupons";
  const isHomePage = location.pathname === "/home" || location.pathname.includes("/home");
  const isProfile = location.pathname === "/profile" || location.pathname === "/member";

  const [, setSearchParams] = useSearchParams();
  
  const handleTabClick = (tab) => {
    if (tab.requiresAuth && !user) { setShowAuthModal(true); return; }
    const tabId = tab.path.includes("tab=") ? tab.path.split("tab=")[1] : null;
    if (tabId && isHomePage) {
      // Already on /home - just update the tab param
      setSearchParams({ tab: tabId });
    } else {
      navigate(tab.path);
    }
  };

  return (
    <nav className="bottom-nav">
      <div className="flex items-center justify-around max-w-md mx-auto">
        {TABS.map((tab) => {
          const tabId = tab.path.includes("tab=") ? tab.path.split("tab=")[1] : null;
          const isActive = tabId
            ? (isHomePage && currentTab === tabId)
            : (location.pathname === tab.path || (tab.path === "/landing" && location.pathname === "/"));

          return (
            <button key={tab.id} onClick={() => handleTabClick(tab)} className="nav-item" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              {isActive && (
                <motion.div layoutId="navIndicator"
                  className="absolute -top-0.5 w-6 h-1 rounded-full"
                  style={{ backgroundColor: C.green }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }} />
              )}
              <motion.div whileTap={{ scale: 0.85 }}
                className="rounded-xl transition-all flex items-center justify-center"
                style={{ backgroundColor: isActive ? C.green + "12" : "transparent", padding: 4, width: 36, height: 36 }}>
                <tab.Icon size={24} active={isActive} />
              </motion.div>
              <span className="text-[9px] font-bold transition-colors"
                style={{ color: isActive ? C.green : "#7A6B5A", fontFamily: "'Figtree', sans-serif" }}>
                {tab.label}
              </span>
            </button>
          );
        })}

        {/* Profile */}
        <button onClick={() => { if (user) navigate("/profile"); else setShowAuthModal(true); }} className="nav-item">
          {isProfile && (
            <motion.div layoutId="navIndicator"
              className="absolute -top-0.5 w-6 h-1 rounded-full"
              style={{ backgroundColor: C.green }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }} />
          )}
          <motion.div whileTap={{ scale: 0.85 }} style={{ padding: 4 }}>
            {user ? (
              <SmileyIcon size={26} active={isProfile} />
            ) : (
              <SmileyIcon size={26} active={false} />
            )}
          </motion.div>
          <span className="text-[9px] font-bold"
            style={{ color: isProfile ? C.green : "#7A6B5A", fontFamily: "'Figtree', sans-serif" }}>
            {user ? "Profil" : "Login"}
          </span>
        </button>
      </div>
    </nav>
  );
}
